import _ from 'underscore';
import Promise from 'bluebird';
import odata from '@bingads-webui-component-legacy/http/channel/odata';
import { getKeywordPlannerSupportedLanguagesByLocationIds, getKeywordPlannerSupportedLocationIdsByLangauges } from '@bingads-webui-campaign-legacy/location-targets/util/constants';
import {
  CONTROL_TYPES,
  URL_OPTION_TYPES,
  KEYWORD_FILTER_WITH_RANGE_TYPES,
  OPERATOR_TYPES,
  NETWORK_TYPES,
  LOCATION_TARGET_LIMIT,
} from '@bingads-webui-campaign-react/keyword-planner-shared';
import { isAllLocationsUnsupported } from './location-util';
import { exampleData } from '../../demo/default/example-data';

const odataLimits = {
  url: 2000,
  location: 100,
  keywordLength: 100,
};

const uri = 'Customers(:advertisercustomer_id)/Accounts(:account_id)/Default.AdGroupSuggestions?$expand=Suggestions';

const requestData = ({
  url,
  keywords,
  targetedLocationInfo,
  languages,
}) => {
  const req = {
    criteria: [],
  };

  let hasOnlyKeyword = true;

  if (url) {
    hasOnlyKeyword = false;
    // KeywordPlanner has a url length limit
    // This url is solely for keyword suggestion purpose and
    // not saved or used by other feature
    const requestUrl = url.substr(0, odataLimits.url);

    req.criteria.push({
      '@odata.type': '#Model.Suggestions.UrlSuggestionCriterion',
      Url: requestUrl,
    });
  }

  if (keywords) {
    const keywordsArray = _.isArray(keywords) ? keywords : [keywords];

    _.each(keywordsArray, (keyword) => {
      req.criteria.push({
        '@odata.type': '#Model.Suggestions.SeedKeywordSuggestionCriterion',
        Keyword: keyword.substr(0, odataLimits.keywordLength),
      });
    });
  }

  let languagesToUse = getKeywordPlannerSupportedLanguagesByLocationIds(_.pluck(targetedLocationInfo.countryLocationInfo, 'countryLocationId'), !hasOnlyKeyword);
  // add parameter when keyword planner support more than one language in multi-language pilot

  if (!_.isEmpty(languages)) {
    // keyword only mode only support one langue for now, will update this after they fix the bug
    if (hasOnlyKeyword && languagesToUse.length > 1) {
      languagesToUse = [_.first(languagesToUse)];
    } else if (!hasOnlyKeyword && languagesToUse.length > 3) {
      // url only support 3 for now, will udpate this after they fix the bug
      languagesToUse = _.first(languagesToUse, 3);
    }
    _.each(languagesToUse, (language) => {
      if (language) {
        req.criteria.push({
          '@odata.type': '#Model.Suggestions.LanguageSuggestionCriterion',
          Language: language,
        });
      }
    });
  }

  if (_.isArray(targetedLocationInfo.locations)) {
    // KeywordPlanner OData has a location count limit
    const languageSupportedLoations = _.isEmpty(languagesToUse) ?
      targetedLocationInfo.locations :
      getKeywordPlannerSupportedLocationIdsByLangauges(languagesToUse, !hasOnlyKeyword);

    const locationIdsSupportedByLanguage = _.compact(_.map(targetedLocationInfo.countryLocationInfo, (item) => {
      if (_.contains(languageSupportedLoations, item.countryLocationId)) {
        return item.locationId;
      }
      return 0;
    }));

    const requestLocations = _.first(
      locationIdsSupportedByLanguage,
      odataLimits.location
    );

    _.each(requestLocations, (locationId) => {
      if (locationId) {
        req.criteria.push({
          '@odata.type': '#Model.Suggestions.LocationSuggestionCriterion',
          LocationId: locationId,
        });
      }
    });
  }

  return req;
};

export const useKeywordSuggestion = () => ({
  keywords,
  url,
  targetedLocationInfo,
  languages,
  scenario = 'campaign creation',
}) => {
  if (window.e2eTestEnvironment) {
    return Promise.resolve({ value: exampleData });
  }

  if (isAllLocationsUnsupported(targetedLocationInfo)) {
    return Promise.resolve(null);
  }

  if (!keywords && !url) {
    return Promise.resolve(null);
  }

  const requestDataObj = requestData({
    keywords, url, targetedLocationInfo, languages,
  });

  if (_.isEmpty(requestDataObj.criteria)) {
    return Promise.resolve(null);
  }

  return odata.post(uri, {
    path: 'AdInsight/V2/',
    data: requestDataObj,
    customHeader: {
      name: 'scenario',
      value: scenario,
    },
  }, (dummy, response, textStatus, xhr) => {
    const currentSourceReqId = (xhr && xhr.getResponseHeader('x-ms-requestid')) || '';
    if (response && response.value) {
      _.each(response.value, (suggestions) => {
        if (suggestions && suggestions.Suggestions) {
          _.each(suggestions.Suggestions, (suggestion) => {
            // eslint-disable-next-line no-param-reassign
            suggestion.RequestId = currentSourceReqId;
          });
        }
      });
    }
  });
};

export const getSearchIdeaModel = ({
  keywords = '',
  url = '',
  targetedLocationInfo,
  languages,
}) => ({
  isIdeas: true,
  [CONTROL_TYPES.SEARCH_IDEA_PRODUCTS]: {
    isValid: true,
    rawKeywords: '',
    parsedKeywords: [keywords],
  },
  [CONTROL_TYPES.LANDING_PAGE]: {
    url,
    isValid: true,
    option: URL_OPTION_TYPES.PAGE,
  },
  [CONTROL_TYPES.LANGUAGE]: _.first(languages),
  [CONTROL_TYPES.LOCATION_LD]: _.first(targetedLocationInfo, LOCATION_TARGET_LIMIT),
  [CONTROL_TYPES.NETWORK]: NETWORK_TYPES.ALL,
  [CONTROL_TYPES.KEYWORD_FILTERS_GROUP]: {
    ..._.object(_.map(KEYWORD_FILTER_WITH_RANGE_TYPES, rangeKeywordFilterType =>
      [rangeKeywordFilterType, { operator: OPERATOR_TYPES.START, value: null, isValid: true }])),
    [CONTROL_TYPES.COMPETITION]: [],
  },
  [CONTROL_TYPES.INCLUDED_KEYWORD]: {
    rawKeywords: '',
    parsedKeywords: [],
  },
  [CONTROL_TYPES.NEGATIVE_KEYWORD]: {
    rawKeywords: '',
    parsedKeywords: [],
  },
  [CONTROL_TYPES.ACCOUNT_KEYWORD_DEDUPE]: true,
  [CONTROL_TYPES.CATEGORY]: {
    categoryId: '',
    isValid: true,
  },
  [CONTROL_TYPES.DATE_RANGE]: {},
});
